import React, { useState } from "react";
import { Lock, AlertCircle } from "lucide-react";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import { useAppState, useActions } from "@/state/legacy_module_api";

export const LockScreen: React.FC = () => {
  const { isLocked, isConfigured } = useAppState().hilo;
  const { unlock } = useActions().hilo;
  const [code, setCode] = useState("");
  const [error, setError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Don't render if not locked or not configured
  if (!isLocked || !isConfigured) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || isSubmitting) return;

    setIsSubmitting(true);
    setError(false);

    try {
      const result = unlock(code);
      if (result === null) {
        // Invalid code (or destroy triggered - page will reload)
        setError(true);
        setCode("");
      }
      // If result is "hi" or "lo", the screen will automatically hide
      // because isLocked will become false
    } catch (e) {
      setError(true);
      setCode("");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-name="lock-screen" className="fixed inset-0 z-[100] bg-background flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-sm space-y-6">
        <div className="flex flex-col items-center space-y-2">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-semibold">Locked</h1>
          <p className="text-muted-foreground text-center">
            Enter your code to unlock
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Input
              data-name="lock-code-input"
              type="password"
              placeholder="Enter code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setError(false);
              }}
              autoFocus
              className={error ? "border-destructive" : ""}
            />
            {error && (
              <div data-name="lock-error" className="flex items-center gap-2 text-destructive text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Invalid code</span>
              </div>
            )}
          </div>

          <Button
            data-name="unlock-button"
            type="submit"
            className="w-full"
            disabled={!code.trim() || isSubmitting}
          >
            {isSubmitting ? "Unlocking..." : "Unlock"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default LockScreen;
